import { useRef } from "react";
import { NumericFormat } from "react-number-format";
import { DECIMAL_SEPARATOR, THOUSAND_SEPARATOR } from "../../utils/formatNumber";

type NumberStepperProps = {
  value: number | null;
  onChange: (v: number | null) => void;
  onBlur?: () => void;
  step: number;
  min?: number;
  max?: number;
  unit?: string;
  placeholder?: string;
  hasError?: boolean;
  ariaLabel?: string;
  disabled?: boolean;
  decimals?: number;
};

function clamp(n: number, min?: number, max?: number): number {
  let out = n;
  if (typeof min === "number" && out < min) out = min;
  if (typeof max === "number" && out > max) out = max;
  return out;
}

export function NumberStepper({
  value,
  onChange,
  onBlur,
  step,
  min,
  max,
  unit,
  placeholder,
  hasError,
  ariaLabel,
  disabled,
  decimals = 2,
}: NumberStepperProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleStep(direction: 1 | -1) {
    if (disabled) return;
    const next = clamp((value ?? 0) + direction * step, min, max);
    onChange(next);
    onBlur?.();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      handleStep(1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      handleStep(-1);
    }
  }

  const borderClass = hasError
    ? "border-red-400"
    : `border-gray-200 dark:border-primary-700 transition-colors ${
        disabled ? "" : "hover:border-primary-400 dark:hover:border-primary-500 focus-within:border-primary-600"
      }`;

  const buttonClass =
    "flex items-center justify-center w-10 flex-shrink-0 text-primary-600 dark:text-white hover:bg-primary-50 dark:hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed select-none";

  return (
    <div className={`flex items-stretch border bg-white dark:bg-primary-800 ${borderClass}`}>
      <button
        type="button"
        tabIndex={-1}
        onClick={() => handleStep(-1)}
        disabled={disabled}
        className={`${buttonClass} border-r border-gray-200 dark:border-primary-700`}
        aria-label="Decrease"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
        </svg>
      </button>
      <NumericFormat
        getInputRef={inputRef}
        className="flex-1 min-w-0 p-3 text-primary-500 dark:text-white bg-transparent text-center focus:outline-none"
        value={value ?? ""}
        thousandSeparator={THOUSAND_SEPARATOR}
        decimalSeparator={DECIMAL_SEPARATOR}
        // THOUSAND_SEPARATOR must NOT be listed here: react-number-format converts any
        // typed allowedDecimalSeparators char into the decimal separator, so "." in this
        // list made "2.000" parse as 2 (#460). Excluded, a typed "." is treated as a
        // thousands character and stripped from the numeric value.
        allowedDecimalSeparators={[DECIMAL_SEPARATOR]}
        decimalScale={decimals}
        allowNegative={false}
        inputMode="decimal"
        onValueChange={(values, sourceInfo) => {
          // Only propagate user edits: programmatic renders of a persisted value with
          // more decimals than decimalScale would otherwise write the rounded display
          // value back into state, corrupting the raw number sent to the API.
          if (sourceInfo.source !== "event") return;
          onChange(values.floatValue ?? null);
        }}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        placeholder={placeholder}
        aria-label={ariaLabel}
        disabled={disabled}
      />
      {unit && (
        <span
          className="self-center pr-1 text-gray-400 dark:text-gray-500 whitespace-nowrap select-none"
          onMouseDown={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          {unit}
        </span>
      )}
      <button
        type="button"
        tabIndex={-1}
        onClick={() => handleStep(1)}
        disabled={disabled}
        className={`${buttonClass} border-l border-gray-200 dark:border-primary-700`}
        aria-label="Increase"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  );
}

export default NumberStepper;
